import { Box, CircularProgress, MenuItem, Select, Typography } from "@mui/material"
import { FunctionComponent, ReactNode, useEffect, useMemo, useState } from "react"
import Button from "@mui/material/Button"
import { useGetGraphDashboardQuery } from "@store/services/dashboardService"
import { DashboardDto } from "@features/results/Result/Dashboard/dashboardModel"
import { DrawerSectionList } from "@features/results/Result/Dashboard/DrawerSectionList"
import companyLogo from "@assets/images/S&P_Global_logo.svg"
import { getDashboardParamUrl, setDashboardParamUrl } from "@features/graph/utils/graphUrl"
import { loadDashboard } from "@features/results/Result/Dashboard/dashboardUtils"
import { store } from "@store/store"
import logger from "@utils/logger"
import { useGetRunResultQuery } from "@store/services/runService"
import { skipToken } from "@reduxjs/toolkit/query"

export enum DashboardMode {
  CompareResults = "CompareResults",
  SingleResult = "SingleResult",
}

interface Props {
  onClose: () => void
  runKeys: string[]
  selectedRunKey: string
  mode: DashboardMode
  slots?: {
    postTitle?: ReactNode
  }
}

export const DrawerDashboard: FunctionComponent<Props> = (props) => {
  const [selectedGroupValue, setSelectedGroupValue] = useState<string>("")
  const [dashboard, setDashboard] = useState<DashboardDto>()
  const [dashboardParsingError, setDashboardParsingError] = useState<string>("")
  const { data: selectedRun, isFetching: isFetchingSelectedRun } = useGetRunResultQuery(
    props.selectedRunKey || skipToken,
  )

  // dashbord is always from the base graph, even if a derived user graph
  const { data: dashboardYaml, isFetching: isFetchingDashboard } = useGetGraphDashboardQuery(
    selectedRun?.base_graph_key || skipToken,
  )

  const isFetching = isFetchingSelectedRun || isFetchingDashboard

  const groups = useMemo(() => {
    if (!dashboard) return []

    switch (props.mode) {
      case DashboardMode.CompareResults:
        return dashboard.CompareResults?.Groups ?? []
      case DashboardMode.SingleResult:
        return dashboard.Groups ?? []
    }
  }, [dashboard, props.mode])

  useEffect(() => {
    if (!dashboard) return

    let selectedGroupName = groups?.[0]?.Name ?? ""

    const groupNameUrlParam = getDashboardParamUrl()
    if (groupNameUrlParam) {
      selectedGroupName =
        groups?.find(({ Name }) => Name === groupNameUrlParam)?.Name || selectedGroupName
    }
    setDashboardParamUrl(selectedGroupName)
    setSelectedGroupValue(selectedGroupName)
  }, [dashboard, groups])

  useEffect(() => {
    try {
      if (!dashboardYaml) return

      const parsedDashboard = loadDashboard(dashboardYaml)
      setDashboard(parsedDashboard)
      setDashboardParsingError("")
    } catch (error) {
      if (error instanceof Error) {
        setDashboard(undefined)
        setDashboardParsingError(error.message)
      }
      logger.error("Error parsing dashboard yaml", error, dashboardYaml)
    }
  }, [dashboardYaml])

  if (isFetching) {
    return (
      <div style={{ height: "100vh", display: "flex", flexDirection: "column", margin: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={() => props.onClose()}>
            Close
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </div>
      </div>
    )
  }

  if (!dashboard) {
    return (
      <Box sx={{ m: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h4">Error with parsing dashboard yaml</Typography>
          <Button variant="contained" onClick={() => props.onClose()}>
            Close
          </Button>
        </Box>
        <Box>{dashboardParsingError}</Box>
        <pre>{dashboardYaml}</pre>
      </Box>
    )
  }

  if (props.mode === DashboardMode.CompareResults && !dashboard.CompareResults) {
    return (
      <Box sx={{ m: 1 }}>
        <Typography variant="h4">
          This dashboard does not contain "CompareResults" block defined
        </Typography>
        <Button sx={{ m: 1 }} variant="contained" onClick={() => props.onClose()}>
          Close
        </Button>
      </Box>
    )
  }

  const selectedGroup = groups.find(({ Name }) => Name === selectedGroupValue)

  if (!selectedGroup) {
    return (
      <Box>
        <Typography variant="h4">Error with parsing dashboard format</Typography>
        <Button variant="contained" onClick={() => props.onClose()}>
          Close
        </Button>
      </Box>
    )
  }

  const handleClose = () => {
    store.dispatch({ type: "dashboard/dashboardClearConfig" })
    props.onClose()
  }

  return (
    <Box sx={{ m: 2 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
        <img src={companyLogo} alt="S&P Global" />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: 20 }}>
            <Typography
              sx={{
                top: "4px",
                fontFamily: "Akkurat-Bold",
                fontStyle: "normal",
                fontWeight: "normal",
                lineHeight: "70px",
                fontSize: "48px",
                letterSpacing: "0.18px",
              }}
            >
              {dashboard.Title}
            </Typography>
            {props.slots?.postTitle}
          </div>
          <div style={{ display: "flex", maxHeight: 50, alignItems: "center" }}>
            <h3 style={{ marginRight: 5 }}>Group</h3>
            <Select
              style={{ width: 200 }}
              value={selectedGroupValue}
              onChange={(e) => {
                setSelectedGroupValue(e.target.value)
                setDashboardParamUrl(e.target.value as string)
              }}
            >
              {groups.map((group, i) => (
                <MenuItem selected={i === 0} id={`group-${i}`} value={group.Name} key={i}>
                  {group.Name}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        <Button variant="contained" onClick={handleClose}>
          Close
        </Button>
      </div>
      <DrawerSectionList
        key={`dashboard-section-list-${selectedGroupValue}`}
        charts={selectedGroup.Charts}
        layouts={selectedGroup.Layout}
        runKeys={props.runKeys}
        selectedRunKey={props.selectedRunKey}
      />
    </Box>
  )
}
